import { useEffect, useState } from "react";
import axios from "axios";
import { captureException } from "@sentry/nextjs";
import classNames from "classnames";
import Tooltip from "@/components/tooltip";
import { InfoSVG } from "@/components/svg";
import { sleep } from "@/utils/time";
import {
  getTotalDeclaredValue,
  FOB_COUNTRIES,
  COURIER_HANDLING_FEE,
  INSURANCE_MULTIPLIER,
} from "@/lib/calc-tax-duties";
import {
  RURAL_SURCHARGE_SGD,
  DIRECT_SIGNATURE_CHARGE_SGD,
  VALENTINES_BOUQUET_PRICE,
} from "@/lib/constants";
import taxData from "@/data/tax-duties-data.json";
import Link from "next/link";
import { getStandardShippingConsumerSurcharge } from "@/lib/shipment/shipping-method";
import { STANDARD_SHIPPING_ENABLED, SHIPPING_METHODS } from "@/lib/constants";
import Option from "./shipping-method/option";
import LoadingSkeleton from "./loading-skeleton";

const standardShippingOption = {
  title: SHIPPING_METHODS.STANDARD.label,
  badgeText: "Cheapest",
  badgeColor: "green",
  duration: SHIPPING_METHODS.STANDARD.duration,
  features: ["Free pick-up", "Last mile tracking"],
};
const expressShippingOption = {
  title: SHIPPING_METHODS.EXPRESS.label,
  badgeText: "Fastest",
  badgeColor: "yellow",
  duration: SHIPPING_METHODS.EXPRESS.duration,
  features: ["Free pick-up", "Customer support", "End to end tracking"],
  premiumFeatures: [
    "Insurance",
    "Pre-paid taxes",
    "Direct signature",
    "Packing services",
  ],
};

export default function Addons({
  formData,
  setFormData,
  setIsLoading: parentSetLoading,
}) {
  const [shippingCosts, setShippingCosts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const setLoading = (val) => {
    parentSetLoading(val);
    setIsLoading(val);
  };
  const [
    standardShippingUnavailableReason,
    setStandardShippingUnavailableReason,
  ] = useState(null);
  const headerStyle = "text-dark-purple font-semibold text-lg my-4";
  const hasTaxRates = Object.keys(taxData).includes(formData.recipientCountry);
  const { shippingMethod = SHIPPING_METHODS.EXPRESS.value } = formData;

  const isStandardShippingEligible =
    !!shippingCosts?.[SHIPPING_METHODS.STANDARD.value];

  const onShippingMethodChange = (shippingMethod) => {
    let newAddOnsFlags = {};

    // Reset the add-ons if standard shipping option is selected
    if (shippingMethod === SHIPPING_METHODS.STANDARD.value) {
      newAddOnsFlags = {
        isInsured: false,
        directSignature: false,
        dutiesPaidBy: "RECIPIENT",
        initialValentinesAmount: 0,
      };
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      ...newAddOnsFlags,
      shippingMethod,
      initialFreightQuote: shippingCosts[shippingMethod],
    }));
  };

  useEffect(() => {
    if (!STANDARD_SHIPPING_ENABLED) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        shippingMethod: SHIPPING_METHODS.EXPRESS.value,
      }));
      return;
    }

    // Reset the add-ons if standard shipping option is selected
    if (shippingMethod === SHIPPING_METHODS.STANDARD.value) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        isInsured: false,
        directSignature: false,
        dutiesPaidBy: "RECIPIENT",
        initialValentinesAmount: 0,
      }));
    }
  }, []);

  useEffect(() => {
    if (!STANDARD_SHIPPING_ENABLED) return;

    const getShippingCosts = async () => {
      setLoading(true);
      try {
        const minFetchTime = 500;
        const startTime = Date.now();
        const responses = await Promise.all([
          axios.post(`/api/shipment/costs`, {
            shipment: {
              ...formData,
              shippingMethod: SHIPPING_METHODS.STANDARD.value,
            },
          }),
          axios.post(`/api/shipment/costs`, {
            shipment: {
              ...formData,
              shippingMethod: SHIPPING_METHODS.EXPRESS.value,
            },
          }),
        ]);
        const freightCosts = responses.map(
          (resp) => resp.data.costs?.freightCost
        );
        if (!responses[0].data?.isValid)
          setStandardShippingUnavailableReason(responses[0].data?.errorMessage);

        const standardShippingSurcharge =
          getStandardShippingConsumerSurcharge();

        setShippingCosts({
          [SHIPPING_METHODS.STANDARD.value]: freightCosts[0]
            ? freightCosts[0] + standardShippingSurcharge
            : null,
          [SHIPPING_METHODS.EXPRESS.value]: freightCosts[1],
        });
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minFetchTime - elapsedTime;

        if (remainingTime > 0) await sleep(remainingTime);
      } catch (err) {
        console.error(err);
        captureException(err);
      }
      setLoading(false);
    };

    getShippingCosts();
  }, []);

  useEffect(() => {
    const getTaxes = async (customValue) => {
      const { data } = await axios.post(`/api/calc-taxes`, {
        packages: formData.packages,
        recipientCountry: formData.recipientCountry,
        recipientPostalCode: formData.recipientPostalCode,
        customValue: customValue,
        isInsured: formData.isInsured,
        freightCost:
          formData.initialFreightQuote +
          (formData.isRural ? RURAL_SURCHARGE_SGD : 0),
        shippingMethod: formData.shippingMethod,
      });
      const { duties, taxes } = data.results;
      const dutyHandlingFee =
        taxes > 0 || duties > 0 ? COURIER_HANDLING_FEE : 0;
      setFormData((formData) => {
        return {
          ...formData,
          taxes,
          duties,
          dutyHandlingFee,
          customValue,
          insuranceAmount: customValue * INSURANCE_MULTIPLIER,
        };
      });
      if (dutyHandlingFee === 0) {
        setFormData((formData) => {
          return {
            ...formData,
            dutiesPaidBy: "RECIPIENT",
          };
        });
      }
    };
    if (formData.recipientCountry) {
      const customValue = getTotalDeclaredValue(formData.packages);
      getTaxes(customValue);
    }
  }, [formData.isInsured]);

  return (
    <div className="flex flex-col text-center w-full">
      {STANDARD_SHIPPING_ENABLED && (
        <div className="flex flex-col bg-white md:rounded-xl shadow pb-6 px-2">
          <p className={headerStyle}>Which shipping option would you like?</p>

          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="w-[86%] sm:w-5/6 mx-auto">
              <div className="flex flex-col md:flex-row gap-y-6 divide-y-2 md:space-x-6 lg:space-x-12 md:divide-none">
                <div className="basis-1/2">
                  <Option
                    disabled={!isStandardShippingEligible}
                    disabledText={standardShippingUnavailableReason}
                    selected={
                      isStandardShippingEligible &&
                      shippingMethod == SHIPPING_METHODS.STANDARD.value
                    }
                    subtext={standardShippingOption.duration}
                    features={standardShippingOption.features}
                    title={standardShippingOption.title}
                    description={
                      <>
                        no add-ons available
                        <br />
                        ship fee:
                      </>
                    }
                    shippingCost={
                      shippingCosts?.[SHIPPING_METHODS.STANDARD.value]
                    }
                    badgeText={standardShippingOption.badgeText}
                    badgeColor={standardShippingOption.badgeColor}
                    onClick={() =>
                      onShippingMethodChange(SHIPPING_METHODS.STANDARD.value)
                    }
                  />
                </div>
                <div className="basis-1/2 pt-8 md:pt-0">
                  <Option
                    selected={
                      !isStandardShippingEligible ||
                      shippingMethod == SHIPPING_METHODS.EXPRESS.value
                    }
                    subtext={expressShippingOption.duration}
                    features={expressShippingOption.features}
                    premiumFeatures={expressShippingOption.premiumFeatures}
                    title={expressShippingOption.title}
                    description={
                      <span>
                        Choose from below add-ons
                        <br />
                        ship fee:
                      </span>
                    }
                    shippingCost={
                      shippingCosts?.[SHIPPING_METHODS.EXPRESS.value]
                    }
                    badgeText={expressShippingOption.badgeText}
                    badgeColor={expressShippingOption.badgeColor}
                    onClick={() =>
                      onShippingMethodChange(SHIPPING_METHODS.EXPRESS.value)
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div
        className={classNames(
          "transition-all duration-300 ease-in-out",
          !STANDARD_SHIPPING_ENABLED ||
            !isStandardShippingEligible ||
            shippingMethod == SHIPPING_METHODS.EXPRESS.value
            ? "opacity-100 h-full"
            : "opacity-0 h-0 overflow-hidden"
        )}
      >
        <div className="flex flex-col bg-white md:rounded-xl shadow my-6 pb-6 px-2">
          <p className={headerStyle}>Would you like to insure this shipment?</p>
          <div className="w-[86%] sm:w-4/5 lg:w-3/4 sm:px-8 mx-auto">
            <div className="flex flex-col md:flex-row gap-y-4 md:space-x-12 justify-between">
              <div className="basis-1/2">
                <button
                  type="button"
                  id="noInsurance"
                  className={`form-input w-full h-[60px]
               ${
                 formData.isInsured === false
                   ? "bg-light-purple text-white border-transparent"
                   : "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
               }
             `}
                  onClick={() => {
                    setFormData((formData) => {
                      return {
                        ...formData,
                        isInsured: false,
                      };
                    });
                  }}
                >
                  No insurance
                </button>
              </div>
              <div className="basis-1/2">
                <button
                  type="button"
                  id="addInsurance"
                  className={`form-input w-full h-[60px]
               ${
                 formData.isInsured === true
                   ? "bg-light-purple text-white border-transparent"
                   : "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
               }
             `}
                  onClick={() => {
                    setFormData((formData) => {
                      return {
                        ...formData,
                        isInsured: true,
                      };
                    });
                  }}
                >
                  Add insurance
                  <br />
                  <span
                    className={`font-semibold ${
                      formData.isInsured === true
                        ? "text-white"
                        : "text-dark-purple"
                    }`}
                  >
                    (+SGD {formData.insuranceAmount.toFixed(2)})
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col bg-white md:rounded-xl shadow mt-6 pb-6 px-2">
          <div className={headerStyle}>
            Would your delivery require a direct signature?
            <Tooltip
              text={
                "Selecting Direct Signature for your shipment means someone at the delivery address must sign for the package, ensuring secure delivery. " +
                "If no one is available, our courier typically attempts delivery up to three times. " +
                "In certain cases, if a delivery cannot be made, your package will be taken to a nearby location for subsequent collection, as indicated on the door tag left by the courier, " +
                "and you'll need a government-issued photo ID to collect your package."
              }
            >
              <InfoSVG />
            </Tooltip>
          </div>
          <div className="w-[86%] sm:w-4/5 lg:w-3/4 sm:px-8 mx-auto">
            <div className="flex flex-col md:flex-row gap-y-4 md:space-x-12 justify-between">
              <div className="basis-1/2">
                <button
                  type="button"
                  id="noDirectSignature"
                  className={`form-input w-full h-[60px]
               ${
                 formData.directSignature === false
                   ? "bg-light-purple text-white border-transparent"
                   : "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
               }
             `}
                  onClick={() => {
                    setFormData((formData) => {
                      return {
                        ...formData,
                        directSignature: false,
                      };
                    });
                  }}
                >
                  No signature required
                </button>
              </div>
              <div className="basis-1/2">
                <button
                  type="button"
                  id="addDirectSignature"
                  className={`form-input w-full h-[60px]
               ${
                 formData.directSignature === true
                   ? "bg-light-purple text-white border-transparent"
                   : "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
               }
             `}
                  onClick={() => {
                    setFormData((formData) => {
                      return {
                        ...formData,
                        directSignature: true,
                      };
                    });
                  }}
                >
                  Signature required
                  <br />
                  <span
                    className={`font-semibold ${
                      formData.directSignature === true
                        ? "text-white"
                        : "text-dark-purple"
                    }`}
                  >
                    (+SGD {DIRECT_SIGNATURE_CHARGE_SGD.toFixed(2)})
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col bg-white md:rounded-xl shadow mt-6 pb-6 px-2">
          <p className={headerStyle}>Who will pay for taxes and duties?</p>
          <div className="w-[86%] sm:w-4/5 lg:w-3/4 sm:px-8 mx-auto">
            {formData.taxes > 0 || formData.duties > 0 ? (
              <div className="flex flex-col md:flex-row gap-y-4 md:space-x-12 justify-between">
                <div className="flex flex-col justify-center basis-1/2">
                  <button
                    type="button"
                    id="receiverPayTax"
                    className={classNames(
                      "form-input w-full h-[60px] place-self-center",
                      formData.dutiesPaidBy === "RECIPIENT"
                        ? "bg-light-purple text-white border-transparent"
                        : "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
                    )}
                    onClick={() => {
                      setFormData((formData) => {
                        return { ...formData, dutiesPaidBy: "RECIPIENT" };
                      });
                    }}
                  >
                    Post-paid, Receiver
                  </button>
                </div>

                <div className="basis-1/2">
                  <button
                    type="button"
                    id="senderPayTax"
                    disabled={!hasTaxRates}
                    className={classNames(
                      "form-input w-full h-[60px]",
                      "disabled:bg-disabled-purple disabled:text-white disabled:border-transparent",
                      formData.dutiesPaidBy === "SENDER"
                        ? "bg-light-purple text-white border-transparent"
                        : "bg-transparent hover:bg-light-purple/10 group hover:border hover:border-light-purple"
                    )}
                    onClick={() => {
                      setFormData((formData) => {
                        return { ...formData, dutiesPaidBy: "SENDER" };
                      });
                    }}
                  >
                    Pre-paid, Sender
                    <br />
                    {hasTaxRates ? (
                      <span
                        className={classNames(
                          "font-semibold",
                          formData.dutiesPaidBy === "SENDER"
                            ? "text-white"
                            : "text-dark-purple"
                        )}
                      >
                        (+SGD{" "}
                        {(
                          (formData.taxes > 0 || formData.duties > 0
                            ? COURIER_HANDLING_FEE
                            : 0) +
                          parseFloat(formData.taxes) +
                          parseFloat(formData.duties)
                        ).toFixed(2)}
                        )
                      </span>
                    ) : (
                      "(Not available)"
                    )}
                  </button>
                </div>
              </div>
            ) : null}
            {formData.dutiesPaidBy === "SENDER" ? (
              <div
                className={classNames(
                  "mt-6 mb-4 p-4 w-full bg-bg-purple rounded-xl",
                  "text-sm font-semibold text-subtext-gray"
                )}
              >
                <p>
                  Import Tax Charges: $
                  <span id="taxes">{formData.taxes.toFixed(2)}</span>
                </p>
                <p>
                  Import Duty Charges: $
                  <span id="duties">{formData.duties.toFixed(2)}</span>
                </p>
                <p>
                  Courier Handling Fee: $
                  <span id="handling-fee">
                    {formData.taxes > 0 || formData.duties > 0
                      ? COURIER_HANDLING_FEE.toFixed(2)
                      : (0).toFixed(2)}
                  </span>
                </p>
                {!FOB_COUNTRIES.includes(formData.recipientCountry) && (
                  <p className="pt-4 text-subtext-gray text-xs font-normal">
                    Please note duties and taxes payable may change if actual
                    parcel dimensions vary from those declared above.
                  </p>
                )}
              </div>
            ) : formData.taxes > 0 || formData.duties > 0 ? (
              <p
                className={classNames(
                  "mt-6 text-center text-xs font-semibold text-subtext-gray",
                  formData.dutiesPaidBy !== "RECIPIENT" ? "hidden" : ""
                )}
              >
                Amount will be determined by the Customs and charged to the
                recipient.
              </p>
            ) : (
              <p
                className={classNames(
                  "mt-2 text-center font-semibold text-subtext-gray",
                  formData.dutiesPaidBy !== "RECIPIENT" ? "hidden" : ""
                )}
              >
                There are no duties and taxes payable for this shipment.
                <br />
                {!FOB_COUNTRIES.includes(formData.recipientCountry) &&
                  "However, please note this may change after verification of parcel dimensions at JustShip."}
              </p>
            )}
          </div>
        </div>
        {process.env.NEXT_PUBLIC_VALENTINES_PROMOTION === "1" && (
          <div className="flex flex-col bg-white md:rounded-xl mt-6 pb-6 px-2">
            <p className={headerStyle}>
              Would you like to add a mini bouquet? 💐 💕
            </p>
            <div className="w-[86%] sm:w-4/5 lg:w-3/4 sm:px-8 mx-auto">
              <div className="flex flex-col md:flex-row gap-y-4 md:space-x-12 justify-between">
                <div className="flex flex-col justify-center basis-1/2">
                  <button
                    type="button"
                    id="addBouquetFalse"
                    className={classNames(
                      "form-input w-full h-[60px] place-self-center",
                      !!!formData.initialValentinesAmount
                        ? "bg-light-purple text-white border-transparent"
                        : "hover:bg-dark-purple hover:text-white hover:border-transparent"
                    )}
                    onClick={() => {
                      setFormData((formData) => {
                        return { ...formData, initialValentinesAmount: 0 };
                      });
                    }}
                  >
                    No bouquet.
                  </button>
                </div>

                <div className="basis-1/2">
                  <button
                    type="button"
                    id="addBouquetTrue"
                    className={classNames(
                      "form-input w-full h-[60px]",
                      "disabled:bg-disabled-purple disabled:text-white disabled:border-transparent",
                      !!formData.initialValentinesAmount
                        ? "bg-light-purple text-white border-transparent"
                        : "hover:bg-dark-purple hover:text-white group hover:border-transparent"
                    )}
                    onClick={() => {
                      setFormData((formData) => {
                        return {
                          ...formData,
                          initialValentinesAmount: VALENTINES_BOUQUET_PRICE,
                        };
                      });
                    }}
                  >
                    {"Yes, I'd like one!"}
                    <br />
                    <span
                      className={classNames(
                        "font-semibold",
                        formData.initialValentinesAmount
                          ? "text-white"
                          : "text-dark-purple"
                      )}
                    >
                      (+SGD ${VALENTINES_BOUQUET_PRICE.toFixed(2)})
                    </span>
                  </button>
                </div>
              </div>
              <p className="mt-6 text-center font-semibold text-subtext-gray">
                See more on our{" "}
                <Link href="/justship-x-spades-and-shovels">
                  <a
                    target="_blank"
                    className="underline text-light-purple hover:text-dark-purple"
                  >
                    partnership with Spades & Shovels
                  </a>
                </Link>
                !
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
